import { ChangeEvent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Chevron } from '@tink/core/src/components/atoms/Icons/Chevron';
import { Caption } from '../../../../../atoms/Caption/Caption';
import { commonMessages } from '../../../../../atoms/CommonMessages/messages';
import { StyledSelect, StyledSelectWrapper } from './SelectField.styles';
import { StyledIconContainer, StyledInputContainer, StyledLabel } from './InputField.styles';
import { messages } from './SelectProviderFieldOption/messages';

type SelectOption = {
  label: string;
  value: string;
};

export type SelectFieldOptions = SelectOption[];

export type SelectFieldProps = {
  name: string;
  options: SelectFieldOptions;
  onChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  value?: string;
  description?: string;
  disabled?: boolean;
  errorCaption?: string;
  helpCaption?: string;
  index?: number;
};

export const SelectField = ({
  name,
  options,
  onChange,
  value,
  description,
  disabled = false,
  helpCaption,
  errorCaption,
  index = 0,
}: SelectFieldProps) => {
  const [selectValue, setSelectValue] = useState<string | undefined>(value);

  const { formatMessage } = useIntl();

  useEffect(() => {
    setSelectValue(value);
  }, [value]);

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectValue(event.currentTarget.value);

    onChange(event);
  };

  return (
    <StyledInputContainer>
      <StyledSelectWrapper>
        <StyledSelect
          id={name}
          data-testid={`input-field-${index}`}
          name={name}
          value={selectValue}
          error={Boolean(errorCaption)}
          disabled={disabled}
          aria-required
          {...(errorCaption || helpCaption
            ? {
                'aria-describedby': `describe-${name}`,
              }
            : {})}
          onChange={handleChange}
          floatingLabel={!!description} // only when description exist
          hasValue={!!selectValue}
        >
          {/*
           * The first one is empty, to not have a default value shown. Safari still displays this option in the list.
           * The value must be an empty string, or Safari uses the option text as value
           */}
          <option aria-label={formatMessage(commonMessages.none)} hidden value="">
            {formatMessage(messages.choose)}
          </option>
          {options.map((option) => (
            <option value={option.value} key={option.value}>
              {option.label}
            </option>
          ))}
        </StyledSelect>
        {description && <StyledLabel htmlFor={name}>{description}</StyledLabel>}
        <StyledIconContainer>
          <Chevron />
        </StyledIconContainer>
      </StyledSelectWrapper>

      {(errorCaption || helpCaption) && (
        <Caption
          id={`describe-${name}`}
          role="alert"
          isError={Boolean(errorCaption)}
          message={errorCaption || helpCaption}
        />
      )}
    </StyledInputContainer>
  );
};
